import * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * The outermost surface. `rounded-xl` and a real border are what separate a
 * card from the page; nested panels inside one step down to `rounded-lg`.
 *
 * Defaults are set so the common case needs no className at all — every
 * override that appears at a call site is a decision this file should have
 * made instead.
 */
function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'border-border bg-bg-elev overflow-hidden rounded-xl border shadow-2xs',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Title block at the top of a card. Carries its own rule and wash, because a
 * card header without them reads as loose content rather than a heading.
 */
function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'border-border-subtle bg-bg-faint flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="card-title"
      className={cn('text-fg m-0 text-base font-semibold', className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-fg-muted mt-0.5 mb-0 text-sm', className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-5 py-4', className)} {...props} />
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'border-border-subtle bg-bg-faint flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3',
        className,
      )}
      {...props}
    />
  )
}

/**
 * A card whose body is a list of records rather than prose — the shape every
 * feed, log and history panel in an admin console takes. Pairs with
 * <CardListRow>; together they are the reason those panels share one rhythm
 * instead of each page inventing its own padding.
 */
function CardList({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="card-list"
      className={cn('divide-border-subtle m-0 list-none divide-y p-0', className)}
      {...props}
    />
  )
}

function CardListRow({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="card-list-row"
      className={cn(
        'hover:bg-bg-subtle flex items-center gap-3 px-5 py-3 transition-colors duration-100 ease-out',
        className,
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardList,
  CardListRow,
}
